import { Building2, Camera, CircleAlert, Download, FileHeart, FileSignature, FileText, FileUp, Landmark, Lock } from 'lucide-react'
import { HOME_COPY } from '../i18n/modes/home'
import type { LanguagePack } from '../i18n/packs'
import type { Copy } from '../i18n/translations'
import type { Language } from '../types/document'
import { LanguageGrid } from './LanguagePicker'

/** Three floating papers in 3D: a letter, a lab report and an agreement. Decorative. */
function PaperStack() {
  return (
    <div className="hero-3d" aria-hidden="true">
      <div className="stage">
        <div className="paper paper-deal">
          <span className="paper-icon"><FileSignature size={22} /></span>
          <i style={{ width: '70%' }} /><i style={{ width: '90%' }} /><i className="hl" style={{ width: '55%' }} /><i style={{ width: '80%' }} />
          <span className="paper-seal" />
        </div>
        <div className="paper paper-med">
          <span className="paper-icon"><FileHeart size={22} /></span>
          <span className="paper-row"><i style={{ width: '45%' }} /><b className="ok" /></span>
          <span className="paper-row"><i style={{ width: '55%' }} /><b className="hi" /></span>
          <span className="paper-row"><i style={{ width: '40%' }} /><b className="ok" /></span>
          <span className="paper-row"><i style={{ width: '50%' }} /><b className="lo" /></span>
        </div>
        <div className="paper paper-gov">
          <span className="paper-icon"><Landmark size={22} /></span>
          <i style={{ width: '60%' }} /><i style={{ width: '92%' }} /><i style={{ width: '84%' }} /><i className="hl" style={{ width: '66%' }} /><i style={{ width: '74%' }} />
          <span className="paper-stamp">✓</span>
        </div>
        <div className="glow" />
      </div>
    </div>
  )
}

export function Home({
  p,
  copy,
  language,
  onLanguage,
  error,
  onTakePhoto,
  onUpload,
  onShareApp,
  onInstall,
  onForOrgs
}: {
  p: LanguagePack
  copy: Copy
  language: Language
  onLanguage: (language: Language) => void
  error: string
  onTakePhoto: () => void
  onUpload: () => void
  onShareApp: () => void
  onInstall?: () => void
  onForOrgs: () => void
}) {
  const h = HOME_COPY[language] ?? HOME_COPY.en
  const modes = [
    { key: 'gov', Icon: FileText, title: h.govTitle, sub: h.govSub },
    { key: 'med', Icon: FileHeart, title: h.medTitle, sub: h.medSub },
    { key: 'deal', Icon: FileSignature, title: h.dealTitle, sub: h.dealSub }
  ]
  return (
    <section className="home-view">
      <header className="hero">
        <PaperStack />
        <h1>
          {h.heroTitle} <span className="accent">{h.heroAccent}</span>
        </h1>
        <p>{h.heroSub}</p>
      </header>

      {error && (
        <div className="error-box" role="alert">
          <CircleAlert size={22} aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      <div className="upload-actions">
        <button className="primary-button btn-camera" onClick={onTakePhoto}>
          <span className="btn-orb"><Camera size={26} strokeWidth={2.2} aria-hidden="true" /></span>
          {p.takePhoto}
        </button>
        <button className="secondary-button btn-file" onClick={onUpload}>
          <FileUp size={20} aria-hidden="true" />
          {p.uploadFile}
        </button>
        <p className="file-note">{p.supportedFiles}</p>
      </div>

      <h2 className="section-label">{h.modesTitle}</h2>
      <div className="mode-cards">
        {modes.map(({ key, Icon, title, sub }) => (
          <button key={key} className={`mode-card mode-${key}`} onClick={onTakePhoto}>
            <span className="mode-orb"><Icon size={24} aria-hidden="true" /></span>
            <strong>{title}</strong>
            <small>{sub}</small>
          </button>
        ))}
      </div>

      <LanguageGrid label={p.chooseLanguage} value={language} onChange={onLanguage} />

      <button className="org-card" onClick={onForOrgs}>
        <span className="mode-orb"><Building2 size={22} aria-hidden="true" /></span>
        <span>
          <strong>{h.forOrgs}</strong>
          <small>{h.forOrgsSub}</small>
        </span>
      </button>

      <p className="privacy-strip">
        <Lock size={16} aria-hidden="true" />
        <span>{p.privacyLine}</span>
      </p>

      <div className="home-links">
        <button className="text-button" onClick={onShareApp}>
          {copy.shareApp}
        </button>
        {onInstall && (
          <button className="text-button" onClick={onInstall}>
            <Download size={16} aria-hidden="true" /> {copy.installApp}
          </button>
        )}
      </div>
    </section>
  )
}
